import { Link, useNavigate } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import { trpc } from '../trpc/client';
import {
  LayoutDashboard, Package, Heart, MessageSquare, Settings, Star,
  TrendingUp, DollarSign, Users, Link as LinkIcon, Award, Shield,
  CheckCircle, Clock, Truck, AlertTriangle, ChevronRight, Plus,
  Eye, BarChart3, FileText, Zap, Globe, Target, Layers
} from 'lucide-react';
import { useState } from 'react';

export default function Dashboard() {
  const { user } = useStore();
  const { t } = useTranslation();

  if (!user) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#1e293b] mb-2">Authentication Required</h2>
          <p className="text-[#64748b] mb-4">Please sign in to access your dashboard</p>
          <Link to="/login" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Route to appropriate dashboard based on role
  if (user.role === 'admin' || user.role === 'super_admin') {
    return <AdminDashboard />;
  }
  if (user.role === 'supplier') {
    return <SupplierDashboard />;
  }
  if (user.ipmeStatus === 'active') {
    return <IPMEDashboard />;
  }
  if (user.role === 'member') {
    return <AffiliateDashboard />;
  }
  return <BuyerDashboard />;
}

// ========================
// BUYER DASHBOARD
// ========================
function BuyerDashboard() {
  const { user } = useStore();
  const { data: ordersData } = trpc.order.list.useQuery({});
  const { data: wishlistData } = trpc.deal.getWishlist.useQuery({});
  const orders = ordersData?.orders || [];
  const wishlist = wishlistData?.deals || [];

  const stats = [
    { label: 'Total Orders', value: orders.length, icon: <Package className="w-5 h-5" />, color: 'bg-[#4338ca]/10 text-[#4338ca]' },
    { label: 'In Progress', value: orders.filter(o => ['paid', 'in_escrow', 'processing', 'shipped'].includes(o.status)).length, icon: <Clock className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600' },
    { label: 'Completed', value: orders.filter(o => o.status === 'completed').length, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Wishlist', value: wishlist.length, icon: <Heart className="w-5 h-5" />, color: 'bg-red-50 text-red-500' },
  ];

  const statusColors: Record<string, string> = {
    pending_payment: 'bg-gray-100 text-gray-600',
    paid: 'bg-blue-50 text-blue-600',
    in_escrow: 'bg-emerald-50 text-emerald-600',
    processing: 'bg-amber-50 text-amber-600',
    shipped: 'bg-purple-50 text-purple-600',
    delivered: 'bg-cyan-50 text-cyan-600',
    receipt_confirmed: 'bg-teal-50 text-teal-600',
    completed: 'bg-emerald-50 text-emerald-600',
    disputed: 'bg-red-50 text-red-600',
    refunded: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="py-6">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">Buyer Dashboard</h1>
          <p className="text-[#64748b]">Welcome back, {user?.firstName}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <p className="text-2xl font-bold text-[#1e293b] font-display">{stat.value}</p>
              <p className="text-xs text-[#64748b]">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Orders */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1e293b]">Recent Orders</h2>
                <Link to="/orders" className="text-sm text-[#c2410c] hover:underline flex items-center gap-1">View All <ChevronRight className="w-4 h-4" /></Link>
              </div>
              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#64748b]">No orders yet. Start browsing deals!</p>
                  <Link to="/deals" className="text-sm text-[#c2410c] font-medium mt-2 inline-block">Browse Deals</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 5).map((order: any) => (
                    <div key={order.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                        <img src={order.deal?.images?.[0]} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1e293b] truncate">{order.deal?.name}</p>
                        <p className="text-xs text-[#64748b]">{order.orderNumber}</p>
                      </div>
                      <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full capitalize ${statusColors[order.status] || 'bg-gray-100'}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                      <p className="text-sm font-semibold text-[#1e293b] shrink-0">${Number(order.totalAmount).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Links */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-[#1e293b] uppercase tracking-wider mb-4">Quick Links</h3>
              <div className="space-y-2">
                {[
                  { label: 'Browse Deals', href: '/deals', icon: <Globe className="w-4 h-4" /> },
                  { label: 'My Wishlist', href: '/wishlist', icon: <Heart className="w-4 h-4" /> },
                  { label: 'Messages', href: '#', icon: <MessageSquare className="w-4 h-4" /> },
                  { label: 'Settings', href: '#', icon: <Settings className="w-4 h-4" /> },
                ].map((link) => (
                  <Link key={link.label} to={link.href} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-[#1e293b] transition-colors">
                    <span className="text-[#64748b]">{link.icon}</span>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Escrow Info */}
            <div className="bg-gradient-to-br from-[#312e81] to-[#4338ca] rounded-2xl p-6 text-white">
              <Shield className="w-8 h-8 mb-3" />
              <h3 className="text-lg font-semibold mb-1">Escrow Protected</h3>
              <p className="text-sm text-white/70 mb-4">Your payments are held securely until you confirm delivery.</p>
              <Link to="#" className="text-xs font-medium text-white/80 hover:text-white underline">Learn more about escrow</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================
// SUPPLIER DASHBOARD
// ========================
function SupplierDashboard() {
  const { user } = useStore();
  const { data: statsData } = trpc.supplier.getDashboardStats.useQuery();
  const { data: ordersData } = trpc.supplier.listOrders.useQuery({});
  const { data: dealsData } = trpc.supplier.listDeals.useQuery();
  const stats = statsData;
  const orders = ordersData?.orders || [];
  const supplierDeals = dealsData?.deals || [];

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="py-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-[#1e293b] font-display">Supplier Dashboard</h1>
            <p className="text-[#64748b]">Manage your deals and orders</p>
          </div>
          <Link to="/dashboard/deals/new" className="px-5 py-2.5 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4" /> Upload New Deal
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Sales', value: stats?.totalSales || 0, icon: <Package className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600' },
            { label: 'Monthly Sales', value: stats?.monthlySales || 0, icon: <TrendingUp className="w-5 h-5" />, color: 'bg-[#4338ca]/10 text-[#4338ca]' },
            { label: 'Supplier Points', value: stats?.totalPoints || 0, icon: <Award className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600' },
            { label: 'Pending Orders', value: stats?.pendingOrders || 0, icon: <Clock className="w-5 h-5" />, color: 'bg-orange-50 text-orange-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <p className="text-2xl font-bold text-[#1e293b] font-display">{stat.value}</p>
              <p className="text-xs text-[#64748b]">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* My Deals */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1e293b]">My Deals</h2>
                <Link to="/dashboard/deals" className="text-sm text-[#c2410c] hover:underline">Manage All</Link>
              </div>
              {supplierDeals.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#64748b]">No deals uploaded yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {supplierDeals.slice(0, 5).map((deal: any) => (
                    <div key={deal.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                        <img src={deal.images?.[0]} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1e293b] truncate">{deal.name}</p>
                        <p className="text-xs text-[#64748b]">{deal.serialNumber}</p>
                      </div>
                      <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full capitalize ${deal.status === 'active' ? 'bg-emerald-50 text-emerald-600' : deal.status === 'pending_review' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-600'}`}>
                        {deal.status.replace('_', ' ')}
                      </span>
                      <p className="text-sm font-semibold text-[#1e293b] shrink-0">${Number(deal.buyerPrice).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Orders */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1e293b]">Recent Orders</h2>
              </div>
              <div className="space-y-3">
                {orders.slice(0, 5).map((order: any) => (
                  <div key={order.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-[#f8fafc] flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-[#4338ca]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1e293b]">{order.orderNumber}</p>
                      <p className="text-xs text-[#64748b]">{order.deal?.name}</p>
                    </div>
                    <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full capitalize ${order.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : order.status === 'shipped' ? 'bg-purple-50 text-purple-600' : 'bg-amber-50 text-amber-600'}`}>
                      {order.status.replace('_', ' ')}
                    </span>
                    <p className="text-sm font-semibold text-[#1e293b] shrink-0">${Number(order.totalAmount).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#c2410c] to-[#9a3412] rounded-2xl p-6 text-white">
              <Award className="w-8 h-8 mb-3" />
              <h3 className="text-lg font-semibold mb-1">Supplier Points</h3>
              <p className="text-3xl font-bold font-display">{stats?.totalPoints || 0}</p>
              <p className="text-sm text-white/70 mt-1">{stats?.monthlySales || 0} points this month</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-[#1e293b] uppercase tracking-wider mb-4">Actions</h3>
              <div className="space-y-2">
                {[
                  { label: 'Upload New Deal', href: '/dashboard/deals/new', icon: <Plus className="w-4 h-4" /> },
                  { label: 'View Analytics', href: '#', icon: <BarChart3 className="w-4 h-4" /> },
                  { label: 'Messages', href: '#', icon: <MessageSquare className="w-4 h-4" /> },
                  { label: 'Settings', href: '#', icon: <Settings className="w-4 h-4" /> },
                ].map((link) => (
                  <Link key={link.label} to={link.href} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-[#1e293b] transition-colors">
                    <span className="text-[#64748b]">{link.icon}</span>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================
// AFFILIATE DASHBOARD
// ========================
function AffiliateDashboard() {
  const { user } = useStore();
  const { data: statsData } = trpc.affiliate.getStats.useQuery();
  const { data: commissionsData } = trpc.affiliate.getCommissions.useQuery();
  const stats = statsData;
  const commissions = commissionsData?.commissions || [];

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="py-6">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">Affiliate Dashboard</h1>
          <p className="text-[#64748b]">Track your commissions and referrals</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Clicks', value: stats?.totalClicks || 0, icon: <Eye className="w-5 h-5" />, color: 'bg-[#4338ca]/10 text-[#4338ca]' },
            { label: 'Conversions', value: stats?.totalConversions || 0, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600' },
            { label: 'Total Earnings', value: `$${(stats?.totalEarnings || 0).toLocaleString()}`, icon: <DollarSign className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600' },
            { label: 'Pending', value: `$${(stats?.pendingEarnings || 0).toLocaleString()}`, icon: <Clock className="w-5 h-5" />, color: 'bg-orange-50 text-orange-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <p className="text-xl font-bold text-[#1e293b] font-display">{stat.value}</p>
              <p className="text-xs text-[#64748b]">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Commissions */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1e293b]">Commission History</h2>
              </div>
              {commissions.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#64748b]">No commissions yet. Start sharing your referral links!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {commissions.map((commission: any) => (
                    <div key={commission.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${commission.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        <DollarSign className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1e293b] capitalize">{commission.commissionType.replace('_', ' ')}</p>
                        <p className="text-xs text-[#64748b]">Order #{commission.order?.orderNumber}</p>
                      </div>
                      <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full capitalize ${commission.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {commission.status.replace('_', ' ')}
                      </span>
                      <p className="text-sm font-semibold text-[#1e293b] shrink-0">${Number(commission.commissionAmount).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#312e81] to-[#4338ca] rounded-2xl p-6 text-white">
              <LinkIcon className="w-8 h-8 mb-3" />
              <h3 className="text-lg font-semibold mb-2">Your Referral Links</h3>
              <p className="text-sm text-white/70 mb-4">Share deals and earn commissions from every purchase made through your link.</p>
              <Link to="/deals" className="block w-full py-2.5 bg-white/10 text-white rounded-xl text-sm font-medium text-center hover:bg-white/20 transition-colors">
                Browse Deals to Share
              </Link>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-[#1e293b] uppercase tracking-wider mb-4">Commission Structure</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">Link Owner (L1)</span>
                  <span className="font-semibold text-[#1e293b]">15% of margin</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">Direct Sponsor</span>
                  <span className="font-semibold text-[#1e293b]">7% of margin</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">Levels 2-10</span>
                  <span className="font-semibold text-[#1e293b]">0.83% each</span>
                </div>
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#64748b]">Min. Payout</span>
                    <span className="font-semibold text-[#c2410c]">$100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================
// IPME DASHBOARD
// ========================
function IPMEDashboard() {
  const { user } = useStore();
  const { data: statsData } = trpc.ipme.getStats.useQuery();
  const { data: tasksData } = trpc.ipme.getTasks.useQuery();
  const stats = statsData;
  const tasks = tasksData?.tasks || [];

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="py-6">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">IPME Dashboard</h1>
          <p className="text-[#64748b]">Instant Purchase Marketing Executive — Manage your tasks</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Tasks', value: stats?.totalTasks || 0, icon: <Layers className="w-5 h-5" />, color: 'bg-[#4338ca]/10 text-[#4338ca]' },
            { label: 'Completed', value: stats?.completedTasks || 0, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600' },
            { label: 'Pending', value: stats?.pendingTasks || 0, icon: <Clock className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600' },
            { label: 'Your Rating', value: stats?.rating || 5.0, icon: <Star className="w-5 h-5" />, color: 'bg-orange-50 text-orange-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <p className="text-2xl font-bold text-[#1e293b] font-display">{stat.value}</p>
              <p className="text-xs text-[#64748b]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1e293b]">Assigned Tasks</h2>
          </div>
          {tasks.length === 0 ? (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-[#64748b]">No tasks assigned yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task: any) => (
                <div key={task.id} className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors border border-gray-50">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${task.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : task.status === 'in_progress' ? 'bg-[#4338ca]/10 text-[#4338ca]' : 'bg-amber-50 text-amber-600'}`}>
                    {task.status === 'completed' ? <CheckCircle className="w-5 h-5" /> : <Target className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1e293b]">{task.dealName}</p>
                    <p className="text-xs text-[#64748b]">{task.supplierName} · Deadline: {task.deadline}</p>
                  </div>
                  <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full capitalize ${task.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : task.status === 'in_progress' ? 'bg-[#4338ca]/10 text-[#4338ca]' : 'bg-amber-50 text-amber-600'}`}>
                    {task.status.replace('_', ' ')}
                  </span>
                  <Link to="#" className="px-3 py-1.5 text-xs font-medium text-[#c2410c] hover:bg-[#c2410c]/5 rounded-lg transition-colors">
                    Review
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ========================
// ADMIN DASHBOARD
// ========================
function AdminDashboard() {
  const { data: analyticsData } = trpc.admin.getAnalytics.useQuery();
  const { data: usersData } = trpc.admin.listUsers.useQuery({ limit: 5 });
  const { data: suppliersData } = trpc.admin.listSuppliers.useQuery();
  const { data: dealsData } = trpc.admin.listDeals.useQuery({ limit: 5 });
  const { data: escrowData } = trpc.admin.getEscrowOverview.useQuery();
  const analytics = analyticsData;
  const recentUsers = usersData?.users || [];
  const pendingSuppliers = (suppliersData?.suppliers || []).filter((s: any) => s.verificationStatus === 'pending');
  const recentDeals = dealsData?.deals || [];

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="py-6">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">Admin Dashboard</h1>
          <p className="text-[#64748b]">Platform overview and management</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Total Users', value: analytics?.totalUsers || 0, icon: <Users className="w-5 h-5" />, color: 'bg-[#4338ca]/10 text-[#4338ca]' },
            { label: 'Suppliers', value: analytics?.totalSuppliers || 0, icon: <Truck className="w-5 h-5" />, color: 'bg-emerald-50 text-emerald-600' },
            { label: 'Active Deals', value: analytics?.activeDeals || 0, icon: <Package className="w-5 h-5" />, color: 'bg-amber-50 text-amber-600' },
            { label: 'Total Orders', value: analytics?.totalOrders || 0, icon: <CheckCircle className="w-5 h-5" />, color: 'bg-purple-50 text-purple-600' },
            { label: 'Revenue', value: `$${((analytics?.totalRevenue || 0) / 1000000).toFixed(1)}M`, icon: <DollarSign className="w-5 h-5" />, color: 'bg-orange-50 text-orange-600' },
            { label: 'Pending KYC', value: analytics?.pendingKyc || 0, icon: <AlertTriangle className="w-5 h-5" />, color: 'bg-red-50 text-red-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.color} flex items-center justify-center mb-2`}>
                {stat.icon}
              </div>
              <p className="text-lg font-bold text-[#1e293b] font-display">{stat.value}</p>
              <p className="text-[11px] text-[#64748b]">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Suppliers */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1e293b]">Pending Supplier Approvals</h2>
              {pendingSuppliers.length > 0 && (
                <span className="px-2.5 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full">{pendingSuppliers.length} pending</span>
              )}
            </div>
            {pendingSuppliers.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-[#64748b]">All suppliers reviewed!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingSuppliers.slice(0, 4).map((supplier: any) => (
                  <div key={supplier.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#312e81] to-[#c2410c] flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {supplier.companyName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1e293b] truncate">{supplier.companyName}</p>
                      <p className="text-xs text-[#64748b]">{supplier.user?.email}</p>
                    </div>
                    <button className="px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors">
                      Approve
                    </button>
                    <button className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                      Reject
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Escrow Overview */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1e293b]">Escrow Overview</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-emerald-50 rounded-xl">
                <p className="text-xs text-emerald-600 mb-1">Total Held</p>
                <p className="text-xl font-bold text-emerald-700 font-display">${((escrowData?.stats?.totalHeld || 0) / 1000000).toFixed(2)}M</p>
              </div>
              <div className="p-4 bg-[#4338ca]/5 rounded-xl">
                <p className="text-xs text-[#4338ca] mb-1">Total Released</p>
                <p className="text-xl font-bold text-[#4338ca] font-display">${((escrowData?.stats?.totalReleased || 0) / 1000000).toFixed(2)}M</p>
              </div>
            </div>
            {escrowData?.stats?.activeDisputes ? (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <p className="text-sm text-red-700">{escrowData.stats.activeDisputes} active disputes require attention</p>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <p className="text-sm text-emerald-700">No active disputes</p>
              </div>
            )}
          </div>

          {/* Recent Users */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1e293b]">Recent Users</h2>
              <Link to="/admin/users" className="text-sm text-[#c2410c] hover:underline">View All</Link>
            </div>
            <div className="space-y-3">
              {recentUsers.slice(0, 5).map((u: any) => (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#312e81] to-[#c2410c] flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {u.firstName[0]}{u.lastName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1e293b]">{u.firstName} {u.lastName}</p>
                    <p className="text-xs text-[#64748b]">{u.email}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize ${u.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-600'}`}>
                    {u.status}
                  </span>
                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#4338ca]/10 text-[#4338ca] capitalize">
                    {u.role}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Deals */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1e293b]">Recent Deals</h2>
              <Link to="/admin/deals" className="text-sm text-[#c2410c] hover:underline">View All</Link>
            </div>
            <div className="space-y-3">
              {recentDeals.slice(0, 5).map((deal: any) => (
                <div key={deal.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
                    <img src={deal.images?.[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1e293b] truncate">{deal.name}</p>
                    <p className="text-xs text-[#64748b]">{deal.supplier?.companyName}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize ${deal.status === 'active' ? 'bg-emerald-50 text-emerald-600' : deal.status === 'pending_review' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-600'}`}>
                    {deal.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
